/* qenova-bundle.js — global + cart, concatenated. */
/* === qenova-global.js === */
/*
 * Qenova global interactions — loaded site-wide
 * - Mobile menu drawer open/close (qenova-mobile-drawer custom element)
 * - Country pill dropdown toggle
 * - IntersectionObserver reveal-on-scroll for `.qfade-up`
 * - Smooth-scroll polyfill (modern browsers already support natively)
 */
(function () {
  'use strict';

  // ============================================================
  // 1) Mobile menu drawer
  // ============================================================
  if (!customElements.get('qenova-mobile-drawer')) {
    class QenovaMobileDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.qmd__panel');
        document.addEventListener('qmd:open', () => this.open());
        this.querySelectorAll('[data-qmd-close]').forEach(el =>
          el.addEventListener('click', e => { e.preventDefault(); this.close(); })
        );
        document.addEventListener('keydown', e => {
          if (e.key === 'Escape' && !this.hidden) this.close();
        });
      }
      open() {
        this.hidden = false;
        requestAnimationFrame(() => this.setAttribute('data-qmd-open', 'true'));
        document.body.classList.add('qmd-open');
        setTimeout(() => { try { this.panel && this.panel.focus(); } catch (e) {} }, 60);
      }
      close() {
        this.setAttribute('data-qmd-open', 'false');
        document.body.classList.remove('qmd-open');
        setTimeout(() => { this.hidden = true; }, 280);
      }
    }
    customElements.define('qenova-mobile-drawer', QenovaMobileDrawer);
  }

  // Click delegation for any [data-qmd-toggle] elements (hamburger buttons)
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-qmd-toggle]');
    if (trigger) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('qmd:open'));
    }
  });

  // ALSO intercept the legacy Vessel/Orreno header hamburger (aria-label="Menü"|"Menu")
  // and route it to the Qenova mobile drawer. Capture-phase listener fires BEFORE
  // any element-level listeners, so we preempt the legacy menu open behavior.
  document.addEventListener('click', function (e) {
    const legacy = e.target.closest('summary[aria-label="Menü"], summary[aria-label="Menu"], button[aria-label="Menü"], button[aria-label="Menu"], .header__icon--menu, [data-menu-drawer-toggle]');
    if (!legacy) return;
    // Don't hijack on desktop where the legacy header shows full nav
    if (window.matchMedia('(min-width: 950px)').matches) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('qmd:open'));
  }, true);

  // ============================================================
  // 2) Country pill dropdown toggle
  // ============================================================
  document.querySelectorAll('[data-qcp]').forEach(pill => {
    const trigger = pill.querySelector('[data-qcp-trigger]');
    const list = pill.querySelector('[data-qcp-list]');
    if (!trigger || !list) return;
    trigger.addEventListener('click', () => {
      const isOpen = list.hidden === false;
      // Close all others
      document.querySelectorAll('[data-qcp-list]').forEach(l => { l.hidden = true; });
      document.querySelectorAll('[data-qcp-trigger]').forEach(t => t.setAttribute('aria-expanded', 'false'));
      if (!isOpen) {
        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // Click outside closes any open country list
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-qcp]')) {
      document.querySelectorAll('[data-qcp-list]').forEach(l => { l.hidden = true; });
      document.querySelectorAll('[data-qcp-trigger]').forEach(t => t.setAttribute('aria-expanded', 'false'));
    }
  });

  // ============================================================
  // 3) IntersectionObserver reveal-on-scroll for `.qfade-up-pending`
  //    Add `.qfade-up-pending` to an element to opt-in; on intersect it
  //    swaps to `.qfade-up` (which has the keyframe animation) for one play.
  // ============================================================
  const fadeTargets = document.querySelectorAll('.qfade-up-pending');
  if (fadeTargets.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('qfade-up-pending');
          entry.target.classList.add('qfade-up');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -80px 0px', threshold: 0.05 });
    fadeTargets.forEach(el => io.observe(el));
  }
})();

/* === Cookieconsent banner accessibility — align aria-labels with visible text === */
(function () {
  function fixCookieAria() {
    const win = document.querySelector('.cc-window');
    if (!win) return false;
    win.querySelectorAll('a[aria-label], button[aria-label]').forEach(el => {
      const txt = (el.textContent || '').trim();
      if (txt) el.setAttribute('aria-label', txt);
    });
    return true;
  }
  if (!fixCookieAria()) {
    const mo = new MutationObserver(() => {
      if (fixCookieAria()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: false });
    setTimeout(() => mo.disconnect(), 8000);
  }
})();

/* === qenova-cart.js === */
/*
 * Qenova cart drawer interactions
 * - Opens on cart link click (event delegation)
 * - Fetches cart via /cart.js, renders line items
 * - +/- qty buttons → POST /cart/change.js
 * - Free-ship progress bar updates from settings.free_ship_threshold_cents
 * - "Oft zusammen gekauft" bundle row fetched from /recommendations/products
 */
(function () {
  'use strict';

  if (customElements.get('qenova-cart-drawer')) return;

  // Read free-ship threshold from a meta tag we'll inject, fallback to 7500 cents
  const FREE_SHIP_THRESHOLD = (() => {
    const meta = document.querySelector('meta[name="qenova:free-ship-threshold-cents"]');
    return meta ? parseInt(meta.content, 10) : 7500;
  })();

  function fmt(cents) {
    try {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '€{{amount}}');
      }
    } catch (e) {}
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  class QenovaCartDrawer extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelector('[data-qcd-items]');
      this.footer = this.querySelector('[data-qcd-footer]');
      this.subtotalEl = this.querySelector('[data-qcd-subtotal]');
      this.progressFill = this.querySelector('[data-qcd-progress-fill]');
      this.progressText = this.querySelector('[data-qcd-progress-text]');
      this.progressRemaining = this.querySelector('[data-qcd-progress-remaining]');
      this.panel = this.querySelector('.qcd__panel');
      this.bundle = this.querySelector('[data-qcd-bundle]');
      this.bundleList = this.querySelector('[data-qcd-bundle-list]');

      document.addEventListener('cart:open', () => this.open());
      this.querySelectorAll('[data-qcd-close]').forEach(el =>
        el.addEventListener('click', e => { e.preventDefault(); this.close(); })
      );
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
    }

    async open() {
      this.hidden = false;
      // next frame so transition runs
      requestAnimationFrame(() => this.setAttribute('data-qcd-open', 'true'));
      document.body.classList.add('qcd-open');
      if (this.items) this.items.innerHTML = '<p class="qcd__loading">Wird geladen…</p>';
      await this.refresh();
      setTimeout(() => { try { this.panel && this.panel.focus(); } catch (e) {} }, 60);
    }

    close() {
      this.setAttribute('data-qcd-open', 'false');
      document.body.classList.remove('qcd-open');
      setTimeout(() => { this.hidden = true; }, 280);
    }

    async refresh() {
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await r.json();
        this.render(cart);
        this.fetchBundle(cart);
      } catch (e) {
        console.error('[qenova-cart-drawer]', e);
        if (this.items) this.items.innerHTML = '<p class="qcd__loading">Warenkorb konnte nicht geladen werden.</p>';
      }
    }

    render(cart) {
      // Update header count badges anywhere on the page
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = String(cart.item_count || 0);
      });

      // Progress
      this.updateProgress(cart.total_price || 0);

      // Empty state
      if (cart.item_count === 0) {
        if (this.items) {
          this.items.innerHTML = '';
          const empty = document.createElement('div');
          empty.className = 'qcd__empty';
          empty.innerHTML = `
            <p class="qcd__empty-text">Ihr Warenkorb ist leer.</p>
            <a class="qbtn-ghost" href="/collections/all">Weiter einkaufen</a>
          `;
          this.items.appendChild(empty);
        }
        if (this.footer) this.footer.hidden = true;
        if (this.bundle) this.bundle.hidden = true;
        return;
      }

      if (this.footer) this.footer.hidden = false;
      if (this.subtotalEl) this.subtotalEl.textContent = fmt(cart.total_price);

      if (!this.items) return;
      this.items.innerHTML = '';
      cart.items.forEach(it => this.items.appendChild(this.lineEl(it)));
    }

    updateProgress(totalCents) {
      const pct = Math.min(100, Math.round((totalCents / FREE_SHIP_THRESHOLD) * 100));
      if (this.progressFill) this.progressFill.style.width = pct + '%';
      const remaining = Math.max(0, FREE_SHIP_THRESHOLD - totalCents);
      if (this.progressText && this.progressRemaining) {
        if (remaining === 0) {
          this.progressText.innerHTML = '<strong>Kostenloser Versand erreicht!</strong> 🎉';
        } else {
          this.progressRemaining.textContent = fmt(remaining);
        }
      }
    }

    lineEl(it) {
      const article = document.createElement('article');
      article.className = 'qcd__line';
      article.dataset.lineKey = it.key;

      const img = it.featured_image && it.featured_image.url ? it.featured_image.url : '';
      const imgWrap = document.createElement('a');
      imgWrap.className = 'qcd__line-img';
      imgWrap.href = it.url;
      if (img) {
        const imgEl = document.createElement('img');
        imgEl.src = img.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?.*)?$/i, '_200x200$1');
        imgEl.alt = it.product_title;
        imgEl.width = 80;
        imgEl.height = 80;
        imgEl.loading = 'lazy';
        imgWrap.appendChild(imgEl);
      }
      article.appendChild(imgWrap);

      const body = document.createElement('div');
      body.className = 'qcd__line-body';
      const title = document.createElement('a');
      title.className = 'qcd__line-title';
      title.href = it.url;
      title.textContent = it.product_title;
      body.appendChild(title);
      if (it.variant_title && it.variant_title !== 'Default Title') {
        const v = document.createElement('div');
        v.className = 'qcd__line-variant';
        v.textContent = it.variant_title;
        body.appendChild(v);
      }
      // Qty controls
      const qty = document.createElement('div');
      qty.className = 'qcd__line-qty';
      const minus = document.createElement('button');
      minus.type = 'button'; minus.className = 'qcd__line-qty-btn'; minus.setAttribute('aria-label', 'Verringern');
      minus.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M5 12h14"/></svg>';
      minus.addEventListener('click', () => this.changeQty(it.key, it.quantity - 1));
      const num = document.createElement('span'); num.className = 'qcd__line-qty-num'; num.textContent = it.quantity;
      const plus = document.createElement('button');
      plus.type = 'button'; plus.className = 'qcd__line-qty-btn'; plus.setAttribute('aria-label', 'Erhöhen');
      plus.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
      plus.addEventListener('click', () => this.changeQty(it.key, it.quantity + 1));
      qty.appendChild(minus); qty.appendChild(num); qty.appendChild(plus);
      body.appendChild(qty);
      article.appendChild(body);

      // Right: trash + price stack
      const right = document.createElement('div');
      right.className = 'qcd__line-right';
      const trash = document.createElement('button');
      trash.type = 'button'; trash.className = 'qcd__line-remove'; trash.setAttribute('aria-label', 'Entfernen');
      trash.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13M10 11v7M14 11v7"/></svg>';
      trash.addEventListener('click', () => this.changeQty(it.key, 0));
      right.appendChild(trash);
      const stack = document.createElement('div');
      stack.className = 'qcd__line-price-stack';
      if (it.original_price > it.final_price) {
        const cmp = document.createElement('span');
        cmp.className = 'qcd__line-compare';
        cmp.textContent = fmt(it.original_line_price);
        stack.appendChild(cmp);
      }
      const p = document.createElement('span');
      p.className = 'qcd__line-price';
      p.textContent = fmt(it.final_line_price);
      stack.appendChild(p);
      right.appendChild(stack);
      article.appendChild(right);

      return article;
    }

    async changeQty(key, qty) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
        });
        await this.refresh();
      } catch (e) { console.error('[qenova-cart change]', e); }
    }

    async fetchBundle(cart) {
      if (!this.bundle || !this.bundleList || cart.item_count === 0) return;
      const firstItemProductId = cart.items[0] && cart.items[0].product_id;
      if (!firstItemProductId) return;
      try {
        const r = await fetch(`/recommendations/products?product_id=${firstItemProductId}&intent=complementary&limit=3&section_id=qenova-bundle`);
        if (!r.ok) return;
        const html = await r.text();
        // Light HTML — try to parse product titles + prices from response (depends on theme)
        // Fallback: hide bundle if response doesn't yield useful data
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const products = doc.querySelectorAll('[data-product-id], .product-card');
        if (products.length === 0) {
          this.bundle.hidden = true;
          return;
        }
        this.bundle.hidden = false;
        // (Full bundle markup generation deferred — needs a server-rendered partial endpoint for clean data)
      } catch (e) {
        // Bundle is non-critical
        this.bundle.hidden = true;
      }
    }
  }

  customElements.define('qenova-cart-drawer', QenovaCartDrawer);

  // Click delegation: any link to /cart opens drawer instead of navigating
  document.addEventListener('click', function (e) {
    const a = e.target.closest('a[href="/cart"], a[href$="/cart"], a[href*="/cart?"]');
    if (!a) return;
    if (a.closest('[data-no-drawer]')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:open'));
  }, true);
})();
